import { IPictureFileMetadataResponse } from '@kcftech/prometheustypescriptapiclient';
import { FileLibraryGroup } from 'components/asset_detail_modal/FilesTab/FilesTab';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { TableColumn } from 'components/data_table/types';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { NoValue } from 'components/no_value/NoValue';
import { FileLibraryPagedDataSource } from 'custom_hooks/data_hooks/data_source/fileLibraryDataSource';
import { FileLibraryActionsCell } from 'pages/Settings/FileLibrary/FileLibraryActionsCell';
import { FolderNameCell } from 'pages/Settings/FileLibrary/FolderNameCell';
import { ImageFileNameCell } from 'pages/Settings/FileLibrary/ImageFileNameCell';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { DebugValue } from 'shared/debug/DebugValue';
import { getId } from 'utils/data';
import { Environment } from 'utils/EnvironmentHelper';

export const getFileUrl = (picture: IPictureFileMetadataResponse) =>
    `${Environment.apiBaseUrl}/api/v3.0/Files/object/${picture.FileName}`;

export type FileLibraryTableProps = Readonly<{
    enableModal?: boolean;
    dataSource: FileLibraryPagedDataSource;
    group?: FileLibraryGroup;
    onFolderClick?: (folderData: IPictureFileMetadataResponse) => void;
    locationId?: string;
    corporationId?: string;
    foldersHierarchy: IPictureFileMetadataResponse[];
}>;

export function FileLibraryTable({
    group,
    enableModal,
    dataSource,
    onFolderClick,
    locationId,
    corporationId,
    foldersHierarchy,
}: FileLibraryTableProps) {
    const { url } = useRouteMatch();
    const { reloadPage } = dataSource;

    const columns = useMemo<TableColumn<IPictureFileMetadataResponse>[]>(
        () => [
            {
                header: () => 'File Name',
                id: 'DisplayName',
                value: ({ rowData }) => (
                    <>
                        {rowData.FileType === 'Picture' ? (
                            <ImageFileNameCell
                                fileData={{
                                    ...rowData,
                                    LocationId: rowData.Location?.LocationId,
                                }}
                                enableModal={enableModal ?? false}
                                url={url}
                            />
                        ) : (
                            <FolderNameCell
                                folderData={rowData}
                                // onFolder click add to breadcrumb
                                onFolderClick={(folderData) =>
                                    onFolderClick?.(folderData)
                                }
                            />
                        )}
                        <DebugValue value={rowData} description="file info" />
                    </>
                ),

                sortOn: 'DisplayName',
                primary: true,
                locked: true,
                valueClassName: 'flex align-center',
            },
            {
                header: () => 'Description',
                id: 'Description',
                value: ({ rowData }) => (
                    <div className="ellipsis-1-line" title={rowData.Description ?? ''}>
                        {rowData.Description ?? <NoValue />}
                    </div>
                ),
                primary: false,
                valueClassName: `${
                    enableModal ? 'max-cell-width-modal' : 'max-cell-width'
                }`,
                locked: true,
            },
            {
                header: () => 'Uploaded',
                id: 'Uploaded',
                value: ({ rowData }) => <DateRelativeToNow date={rowData.CreationDate} />,
                sortOn: 'CreationDate',
                primary: false,
                locked: true,
            },
            {
                header: () => 'Actions',
                id: 'Actions',
                locked: true,
                value: ({ rowData }) => (
                    <FileLibraryActionsCell
                        group={group}
                        enableModal={enableModal ?? false}
                        rowData={rowData}
                        onChange={reloadPage}
                        locationId={locationId}
                        corporationId={corporationId}
                        foldersHierarchy={foldersHierarchy}
                    />
                ),
            },
        ],
        [
            enableModal,
            url,
            onFolderClick,
            group,
            reloadPage,
            locationId,
            corporationId,
            foldersHierarchy,
        ]
    );

    return (
        <DataSourceTable
            dataSource={dataSource}
            rowKey={getId}
            columns={columns}
            noResultMessage="No files found."
        />
    );
}
